
import React, { useState, useRef, useEffect } from 'react';
import Tesseract from 'tesseract.js';
import Webcam from 'react-webcam';
import axios from 'axios';
import * as API from "../../Endpoints/endpoint";
import '../Scanner/scanner.css';

const Scanner = () => {
  const webcamRef = useRef(null);
  const [detectedText, setDetectedText] = useState('');
  const [detectedNumber, setDetectedNumber] = useState(null);
  const [manualNumber, setManualNumber] = useState('');
  const [fetchedData, setFetchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('en');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const synthRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef(null);

  const captureAndScan = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      alert("Camera not ready, try again.");
      return;
    }

    Tesseract.recognize(imageSrc, 'eng', { logger: m => console.log(m) })
      .then(({ data: { text } }) => {
        const cleanText = text.trim();
        console.log("Full OCR Text:", cleanText);
        setDetectedText(cleanText);
        const srnoMatch = cleanText.match(/\d+/);
        const srno = srnoMatch ? srnoMatch[0] : null;
        if (!srno) {
          alert("No serial number found. You can enter it manually below.");
          setDetectedNumber(null);
          return;
        }
        console.log("Detected SRNO:", srno);
        setDetectedNumber(srno);
        setManualNumber('');
      })
      .catch(err => {
        console.error("OCR error:", err);
        alert("Failed to scan image.");
        setDetectedNumber(null);
      });
  };

  const fetchRecord = async () => {
    const finalSrno = manualNumber || detectedNumber;
    if (!finalSrno) {
      alert('Please scan or enter a SRNO first.');
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API.GET_POSTER_BY_ID}?srno=${finalSrno}&language=${language}`);
      console.log('Fetched data:', res.data);
      setFetchedData(res.data);
    } catch (err) {
      console.error('Error fetching record:', err);
      alert(err.response?.data?.message || 'Failed to fetch record.');
      setFetchedData(null);
    } finally {
      setLoading(false);
    }
  };

  const openPdfPreview = (driveUrl) => {
    const fileIdMatch = driveUrl.match(/\/d\/(.*?)\//);
    if (fileIdMatch) {
      const fileId = fileIdMatch[1];
      window.open(`https://drive.google.com/file/d/${fileId}/preview`, '_blank');
    } else {
      alert("Invalid file link. Must be a valid Google Drive file URL.");
    }
  };

  const startSpeaking = () => {
    if (!fetchedData || !fetchedData.summary) return;
    if (language === 'mr') return;
    if (synthRef.current.speaking) synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(fetchedData.summary);
    utterance.lang = language === 'en' ? 'en-US' : (language === 'hi' ? 'hi-IN' : 'en-US');
    const voices = synthRef.current.getVoices();
    let voice = voices.find(v => v.lang.startsWith(utterance.lang)) || voices.find(v => v.lang.startsWith('en'));
    if (voice) utterance.voice = voice;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
    utteranceRef.current = utterance;
    setIsSpeaking(true);
  };

  useEffect(() => {
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = () => synthRef.current.getVoices();
    }
  }, []);

  const stopSpeaking = () => {
    if (synthRef.current.speaking) synthRef.current.cancel();
    setIsSpeaking(false);
  };

  const handleVoiceToggle = (e) => {
    if (e.target.checked) startSpeaking();
    else stopSpeaking();
  };

  const handleGoBack = () => {
    stopSpeaking();
    setFetchedData(null);
    setDetectedText('');
    setDetectedNumber(null);
    setManualNumber('');
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axios.get(API.GET_LANGUAGE);
        setAvailableLanguages(res.data);
      } catch (err) {
        console.error('Failed to fetch languages:', err);
      }
    };
    fetchLanguages();
  }, []);

  return (
    <div className="scanner-container">
      {!fetchedData ? (
        <>
          <h2 className="scanner-title">Scan Poster</h2>
          <div className="language-select">
            <label htmlFor="language-select" style={{ marginRight: 8 }}>Choose Language:</label>
            <select id="language-select" value={language} onChange={(e) => setLanguage(e.target.value)} style={{ padding: '4px 8px', fontSize: '16px' }}>
              {availableLanguages.map((lang, idx) => (
                <option key={idx} value={lang}>{lang}</option>
              ))}
            </select>
          </div>
          <div className="webcam-container">
            <Webcam audio={false} ref={webcamRef} screenshotFormat="image/png" width={320} height={240} videoConstraints={{ facingMode: "environment" }} style={{ borderRadius: '12px' }} />
            <button onClick={captureAndScan} className="scan-button" style={{ marginTop: '16px' }}>📸 Scan SRNO</button>
          </div>
          <div className="status-box"><strong>Detected Text:</strong> {detectedText || 'None'}</div>
          <div className="status-box"><strong>Detected Number (SRNO):</strong> {detectedNumber || 'None'}</div>
          <div>
            <label className="textarea-label">Enter SRNO manually (if scan incorrect):</label>
            <textarea value={manualNumber} onChange={(e) => setManualNumber(e.target.value)} className="manual-textarea"></textarea>
          </div>
          <div className="final-srno">Final SRNO to use: <span>{manualNumber || detectedNumber || 'None'}</span></div>
         <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={fetchRecord}
            className="scan-button"
            disabled={loading}
          >
            {loading ? '⏳ Loading...' : '📄 Get Info'}
          </button>

          <button
            onClick={() => window.location.href = "/zones"}
            className="scan-button"
          >
            ⬅️ Go Back
          </button>
        </div>

        </>
      ) : (
        <>
          <h2 className="scanner-title">📑 Details</h2>
          {(language === 'en' || language === 'hi') && (
            <div style={{ position: 'fixed', top: 20, right: 20, padding: 10, borderRadius: 8, boxShadow: '0 0 5px rgba(0,0,0,0.2)' }}>
              <label><input type="checkbox" checked={isSpeaking} onChange={handleVoiceToggle} style={{ marginRight: 6 }} /> 🔊 Speak Summary</label>
            </div>
          )}
          <div className="result-card">
            <h3>{fetchedData.chartname}</h3>
            <p><strong>Summary:</strong> {fetchedData.summary}</p>
            <p><strong>Video:</strong> <a href={fetchedData.video} target="_blank" rel="noopener noreferrer">Watch Video</a></p>
          </div>
          {fetchedData.photopdf && (<div style={{ marginTop: '20px' }}><button onClick={() => openPdfPreview(fetchedData.photopdf)} className="scan-button">📄 View PDF</button></div>)}
          {fetchedData.voice && (<div style={{ marginTop: '20px' }}><button onClick={() => window.open(fetchedData.voice, '_blank')} className="scan-button">🎙️ Voice</button></div>)}
          <button className="scan-button" style={{ marginTop: '24px' }} onClick={handleGoBack}>⬅️ Go Back</button>
        </>
      )}
    </div>
  );
};

export default Scanner;
